import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();
const isAdmin = currentUser?.role === 'admin';

const empty = (msg) => (
  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{msg}</div>
);

export default function AdminRecipients() {
  const [recipients, setRecipients]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [recipientForm, setRecipientForm]     = useState({
    name: '', bank_name: '', routing_number: '', account_number: '', account_type: 'checking', notes: '',
  });
  const [recipientOtpActive, setRecipientOtpActive]       = useState(false);
  const [recipientOtpCode, setRecipientOtpCode]           = useState('');
  const [recipientOtpExpiry, setRecipientOtpExpiry]       = useState(null);
  const [recipientOtpError, setRecipientOtpError]         = useState('');
  const [recipientOtpVerifying, setRecipientOtpVerifying] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchRecipients = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/agents/finance/recipients`, { headers });
      setRecipients(res.data || []);
    } catch (err) {
      console.error('Recipients fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecipients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Two-step add: request OTP (step 1), verify (step 2). The verify endpoint
  // is the only thing that writes a recipient to the whitelist.
  const handleAddRecipientOtp = async () => {
    setRecipientOtpError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/agents/finance/recipients/request-otp`,
        recipientForm, { headers }
      );
      setRecipientOtpActive(true);
      setRecipientOtpExpiry(res.data.expiresAt);
      setRecipientOtpCode('');
    } catch (err) {
      setRecipientOtpError(err.response?.data?.error || err.message);
    }
  };

  const handleRecipientOtpVerify = async () => {
    if (recipientOtpCode.length !== 6) return;
    setRecipientOtpVerifying(true);
    setRecipientOtpError('');
    try {
      await axios.post(
        `${API}/api/admin/agents/finance/recipients/verify-and-add`,
        { code: recipientOtpCode }, { headers }
      );
      setRecipientOtpActive(false);
      setShowAddRecipient(false);
      setRecipientForm({ name: '', bank_name: '', routing_number: '', account_number: '', account_type: 'checking', notes: '' });
      await fetchRecipients();
    } catch (err) {
      setRecipientOtpError(err.response?.data?.error || err.message);
    } finally {
      setRecipientOtpVerifying(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Recipients</h1>
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading…</div>}

        {!loading && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Approved payees that wires can be sent to. Routing &amp; account numbers are never displayed.
              </div>
              {isAdmin && (
                <button
                  onClick={() => { setShowAddRecipient(s => !s); setRecipientOtpActive(false); setRecipientOtpError(''); }}
                  style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Add Recipient
                </button>
              )}
            </div>

            {showAddRecipient && !recipientOtpActive && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 16 }}>New Payment Recipient</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { key: 'name', label: 'Recipient Name', required: true },
                    { key: 'bank_name', label: 'Bank Name' },
                    { key: 'routing_number', label: 'Routing Number', required: true },
                    { key: 'account_number', label: 'Account Number', required: true },
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                        {field.label}{field.required && ' *'}
                      </label>
                      <input
                        type={['routing_number', 'account_number'].includes(field.key) ? 'password' : 'text'}
                        value={recipientForm[field.key]}
                        onChange={e => setRecipientForm(f => ({ ...f, [field.key]: e.target.value }))}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }}>Account Type</label>
                    <select
                      value={recipientForm.account_type}
                      onChange={e => setRecipientForm(f => ({ ...f, account_type: e.target.value }))}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}>
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                      <option value="wire">Wire</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                    <input
                      type="text"
                      value={recipientForm.notes}
                      onChange={e => setRecipientForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="e.g. Austin Title Company — closing account"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {recipientOtpError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991b1b', marginTop: 12 }}>
                    {recipientOtpError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowAddRecipient(false); setRecipientOtpError(''); }}
                    style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleAddRecipientOtp}
                    disabled={!recipientForm.name || !recipientForm.routing_number || !recipientForm.account_number}
                    style={{
                      background: (!recipientForm.name || !recipientForm.routing_number || !recipientForm.account_number) ? '#9ca3af' : '#3a6ad9',
                      color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600,
                      cursor: (!recipientForm.name || !recipientForm.routing_number || !recipientForm.account_number) ? 'default' : 'pointer',
                    }}>
                    Send Verification Code →
                  </button>
                </div>
              </div>
            )}

            {showAddRecipient && recipientOtpActive && (
              <div style={{ background: '#fff', border: '2px solid #3a6ad9', borderRadius: 12, padding: '24px', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Verify New Recipient</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  A 6-digit code has been sent to your email. Enter it below to add this recipient.
                </div>
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  value={recipientOtpCode}
                  onChange={e => setRecipientOtpCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter' && recipientOtpCode.length === 6 && !recipientOtpVerifying) handleRecipientOtpVerify(); }}
                  placeholder="000000" autoFocus
                  style={{ width: '100%', textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 12, fontFamily: 'monospace', padding: '14px', border: '2px solid #e5e7eb', borderRadius: 10, boxSizing: 'border-box', marginBottom: 12, color: '#111827' }}
                />
                {recipientOtpExpiry && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12, textAlign: 'center' }}>
                    Code expires at {new Date(recipientOtpExpiry).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}
                {recipientOtpError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991b1b', marginBottom: 12 }}>
                    {recipientOtpError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button disabled={recipientOtpVerifying || recipientOtpCode.length !== 6} onClick={handleRecipientOtpVerify}
                    style={{
                      flex: 1,
                      background: (recipientOtpVerifying || recipientOtpCode.length !== 6) ? '#9ca3af' : '#3a6ad9',
                      color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600,
                      cursor: (recipientOtpVerifying || recipientOtpCode.length !== 6) ? 'default' : 'pointer',
                    }}>
                    {recipientOtpVerifying ? 'Adding…' : 'Verify & Add Recipient'}
                  </button>
                  <button onClick={() => { setRecipientOtpActive(false); setRecipientOtpCode(''); setRecipientOtpError(''); }}
                    style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {recipients.length === 0 && !showAddRecipient && empty('No approved recipients yet — add one above')}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
              {recipients.map(r => (
                <div key={r.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{r.name}</div>
                    <span style={{ fontSize: 10, fontWeight: 600, background: r.verified ? '#dcfce7' : '#fef9c3', color: r.verified ? '#166534' : '#854d0e', borderRadius: 999, padding: '2px 8px' }}>
                      {r.verified ? '✓ Verified' : 'Pending'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{r.bank_name || 'Bank not specified'}</div>
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                    {(() => { const t = r.account_type || 'checking'; return t.charAt(0).toUpperCase() + t.slice(1); })()} ••••{r.account_number_last4}
                  </div>
                  {r.notes && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, lineHeight: 1.4 }}>{r.notes}</div>}
                  {isAdmin && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <button
                        onClick={() => {
                          if (window.confirm(`Deactivate ${r.name}? They cannot receive payments after this.`)) {
                            axios.patch(`${API}/api/admin/agents/finance/recipients/${r.id}/deactivate`, {}, { headers }).then(() => fetchRecipients());
                          }
                        }}
                        style={{ background: 'none', color: '#9ca3af', border: 'none', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                        Deactivate
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
